import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  MessageSquarePlus, Send, Loader2, CheckCircle2, Clock, 
  MessageCircle, Lightbulb, Bug, HelpCircle, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function FeedbackButton() {
  const { token, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('new');
  const [loading, setLoading] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    subject: '',
    message: '',
    category: 'suggestion'
  });

  // Listen for custom event from Header
  useEffect(function() {
    function handleOpenFeedback() {
      setIsOpen(true);
    }
    window.addEventListener('openFeedback', handleOpenFeedback);
    return function() {
      window.removeEventListener('openFeedback', handleOpenFeedback);
    };
  }, []);

  // Fetch user feedbacks
  async function fetchFeedbacks() {
    if (!token) return;
    setLoadingHistory(true);
    try {
      var response = await axios.get(API_URL + '/api/feedback', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setFeedbacks(response.data.feedbacks || []);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoadingHistory(false);
    }
  }

  useEffect(function() {
    if (isOpen && activeTab === 'history') {
      fetchFeedbacks();
    }
  }, [isOpen, activeTab, token]);

  async function handleSubmit() {
    if (!form.subject.trim()) {
      toast.error('Veuillez entrer un sujet');
      return;
    }
    if (!form.message.trim()) {
      toast.error('Veuillez entrer votre message');
      return;
    }

    setLoading(true);
    try {
      await axios.post(API_URL + '/api/feedback', form, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      toast.success('Merci pour votre feedback ! Nous vous répondrons rapidement.');
      setForm({ subject: '', message: '', category: 'suggestion' });
      setActiveTab('history');
      fetchFeedbacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  }

  function getCategoryIcon(category) {
    switch (category) {
      case 'suggestion': return <Lightbulb className="h-4 w-4 text-amber-500" />;
      case 'bug': return <Bug className="h-4 w-4 text-red-500" />;
      case 'question': return <HelpCircle className="h-4 w-4 text-blue-500" />;
      default: return <MessageCircle className="h-4 w-4 text-slate-500" />;
    }
  }

  function getStatusBadge(status) {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
            <Clock className="h-3 w-3" /> En attente
          </span>
        );
      case 'responded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
            <CheckCircle2 className="h-3 w-3" /> Répondu
          </span>
        );
      case 'closed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
            Clôturé
          </span>
        );
      default:
        return null;
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    var date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Don't render if not authenticated
  if (!isAuthenticated) return null;

  var feedbackItems = feedbacks.map(function(fb) {
    return (
      <div key={fb.id} className="border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {getCategoryIcon(fb.category)}
            <span className="font-medium text-slate-900 text-sm">{fb.subject}</span>
          </div>
          {getStatusBadge(fb.status)}
        </div>
        
        <p className="text-sm text-slate-600">{fb.message}</p>
        
        <p className="text-xs text-slate-400">{formatDate(fb.created_at)}</p>
        
        {fb.response && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">A</span>
              </div>
              <span className="text-sm font-medium text-blue-900">Réponse de l'équipe</span>
            </div>
            <p className="text-sm text-blue-800">{fb.response}</p>
            <p className="text-xs text-blue-500 mt-2">{formatDate(fb.responded_at)}</p>
          </div>
        )}
      </div>
    );
  });

  return (
    <>
      {/* Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-blue-600" />
              Feedback utilisateur
            </DialogTitle>
            <DialogDescription>
              Aidez-nous à améliorer 5minCompta avec vos retours et suggestions.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="new">Nouveau feedback</TabsTrigger>
              <TabsTrigger value="history">Mes feedbacks ({feedbacks.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="new" className="flex-1 overflow-auto space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={function(v) { setForm({ ...form, category: v }); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suggestion">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-amber-500" />
                        Suggestion d'amélioration
                      </div>
                    </SelectItem>
                    <SelectItem value="bug">
                      <div className="flex items-center gap-2">
                        <Bug className="h-4 w-4 text-red-500" />
                        Signaler un bug
                      </div>
                    </SelectItem>
                    <SelectItem value="question">
                      <div className="flex items-center gap-2">
                        <HelpCircle className="h-4 w-4 text-blue-500" />
                        Question
                      </div>
                    </SelectItem>
                    <SelectItem value="autre">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4 text-slate-500" />
                        Autre
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Sujet *</Label>
                <Input
                  placeholder="Ex: Améliorer l'export PDF"
                  value={form.subject}
                  onChange={function(e) { setForm({ ...form, subject: e.target.value }); }}
                />
              </div>

              <div className="space-y-2">
                <Label>Votre message *</Label>
                <Textarea
                  placeholder="Décrivez votre suggestion, bug ou question en détail..."
                  value={form.message}
                  onChange={function(e) { setForm({ ...form, message: e.target.value }); }}
                  rows={5}
                  className="resize-none"
                />
              </div>

              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-500">
                  💡 Vos retours sont précieux et nous aident à améliorer l'application. 
                  Nous vous répondrons dans les plus brefs délais.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="history" className="flex-1 overflow-auto mt-4">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : feedbacks.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Vous n'avez pas encore envoyé de feedback.</p>
                  <p className="text-sm mt-1">Vos retours sont les bienvenus !</p>
                </div>
              ) : (
                <div className="space-y-4 pr-2">
                  {feedbackItems}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {activeTab === 'new' && (
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={function() { setIsOpen(false); }}>
                Annuler
              </Button>
              <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Envoyer
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FeedbackButton;
